import { AmbientLight, Clock, DirectionalLight, Fog, HemisphereLight, PerspectiveCamera, Scene, Vector2, WebGLRenderer, Mesh, PlaneGeometry, CustomBlending, ShadowMaterial } from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import Human from './Human'
import { OutlinePass } from './OutlinePass'

export default class Stage3D {
  constructor (container, gltf) {
    
    this.container = container


    this.createEngine()
    this.createLights()
    this.initPostProcessing(this.stageWidth, this.stageHeight)

    this.handleWindowResize()


    
    this.human = new Human(gltf)
    this.scene.add(this.human.mesh)

    this.outlinePass.selectedObjects = [this.human.mesh]
    // place model on floor level
    // this.human.mesh.position.setY(-2)

    this.floor = new Mesh(
      new PlaneGeometry(1000, 1000, 1, 1),
      new ShadowMaterial({
        color: 0x000066,
        // blending: CustomBlending,
      })
    )
    this.floor.rotateX(-Math.PI/2)
    // this.floor.position.setY(-2)
    this.floor.castShadow = false
    this.floor.receiveShadow = true
    this.scene.add(this.floor)

    this.clock = new Clock()

    this.camera.position.x = 0
    this.camera.position.y = 2
    this.camera.position.z = 20
    this.camera.lookAt(
      this.human.mesh.position.x,
      this.human.mesh.position.y ,
      this.human.mesh.position.z
    )

    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.enableDamping = true // an animation loop is required when either damping or auto-rotation are enabled
    this.controls.dampingFactor = 0.05
    this.controls.screenSpacePanning = false
    this.controls.minDistance = 5
    this.controls.maxDistance = 50
    this.controls.maxPolarAngle = Math.PI / 2

    this.scene.rotation.y = 1
    this.scene.position.setY(-2)
    this.loop()
  }


  initPostProcessing (renderTargetWidth, renderTargetHeight) {
    // init the postprocessing pipeline
    this.composer = new EffectComposer(this.renderer)
    // main render pass for the 3d model and shadows
    const renderPass = new RenderPass(this.scene, this.camera)
    // add the render pass to post process pipeline
    this.composer.addPass(renderPass)
    // outline pass
    this.outlinePass = new OutlinePass(new Vector2(this.stageWidth, this.stageHeight), this.scene, this.camera)
    // we only want the outline on the human model, not the floor
    this.outlinePass.selectedObjects = [this.scene]
    // add the outline pass to post process pipeline
    this.composer.addPass(this.outlinePass)
  }

  loop = () => {
    window.requestAnimationFrame(this.loop)
    this.render()
    this.controls && this.controls.update()
  }

  render () {
    
    this.renderer.setClearColor(0x000000, 0)
    this.renderer.setRenderTarget(null)
    this.renderer.clear(true, true, true)
    this.renderer.clearDepth()

    let delta = Math.max(0, Math.min(1, this.clock.getDelta()))
    // this.scene.rotation.y += delta
    // this.human.mesh.rotation.y += delta * 0.1
    
    this.human.update(delta, this.clock.elapsedTime)    
    this.composer.render()
  }
  
  createEngine () {
    this.scene = new Scene()

    this.scene.fog = new Fog(0x111111, 50, 100)
  
    const aspectRatio = this.stageWidth / this.stageHeight
    const fieldOfView = 40
    const nearPlane = 1
    const farPlane = 1500
    this.camera = new PerspectiveCamera(
      fieldOfView,
      aspectRatio,
      nearPlane,
      farPlane
    )
  
    this.renderer = new WebGLRenderer({
      antialias: true,
      alpha: true
    })
    // this.renderer.autoClear = true
    // this.renderer.setPixelRatio(0.2)
    // this.renderer.setPixelRatio(0.5)
    this.renderer.setPixelRatio(window.devicePixelRatio)
    // this.renderer.setClearColor(0x111111, 1)
    this.renderer.setClearColor(0xffffff, 0)
    // this.renderer.setClearColor(0xffffff, 1)
    
    this.renderer.setSize(this.stageWidth, this.stageHeight)
    
    this.renderer.shadowMap.enabled = true
    
    this.container.appendChild(this.renderer.domElement)
    
    window.addEventListener('resize', this.handleWindowResize, false)
    
    // this.controls = new OrbitControls(this.camera, this.renderer.domElement)


    // this.stats = new Stats()
    // this.container.appendChild(this.stats.dom)
    
    // const grid = new GridHelper(1000, 20)
    // this.scene.add(grid)

  }

  handleWindowResize = () => {
    // const {width, height} = this.renderer.domElement.getBoundingClientRect()
    const width = window.innerWidth * 0.96 // + 2% margin on both sides in CSS
    const height = window.innerHeight * 0.96 // + 2% margin on both sides in CSS
    this.stageWidth = width
    this.stageHeight = height
    this.renderer.setSize(this.stageWidth, this.stageHeight)
    this.camera.aspect = this.stageWidth / this.stageHeight
    this.camera.updateProjectionMatrix()

    this.composer.setSize(this.stageWidth, this.stageHeight)
    // this.effectFXAA && this.effectFXAA.uniforms[ 'resolution' ].value.set(1 / this.stageWidth, 1 / this.stageHeight)

  }

  createLights () {
    // A hemisphere light is a gradient colored light; 
    // the first parameter is the sky color, the second parameter is the ground color, 
    // the third parameter is the intensity of the light
    // this.hemisphereLight = new HemisphereLight(0xffffff, 0x000000, .5  )
    // const hemisphereLightHelper = new THREE.HemisphereLightHelper(this.hemisphereLight, 2)
    // this.scene.add(hemisphereLightHelper)
    
    // an ambient light modifies the global color of a scene and makes the shadows softer
    // this.ambientLight = new AmbientLight(0xdc8874, .5)
    // this.ambientLight = new AmbientLight(0xaa2211, .75)
    // this.ambientLight = new AmbientLight(0xffe72b, .5)
    
    // A directional light shines from a specific direction. 
    // It acts like the sun, that means that all the rays produced are parallel. 
    this.shadowLight = new DirectionalLight(0xffffff, 1)
    // this.shadowLight = new DirectionalLight(0xaaffff, 0.5)
    // this.shadowLight = new DirectionalLight(0xffe72b, 0.5)
    // this.shadowLightHelper = new DirectionalLightHelper(this.shadowLight, 1000, 0xff00ff)
    // this.scene.add(this.shadowLightHelper)

    // Set the direction of the light  
    this.shadowLight.position.set(25, 25, 10)
    // this.shadowLight.position.set(0, 3, 0)
    this.shadowLight.lookAt(0,0,0)
  
    // Allow shadow casting 
    this.shadowLight.castShadow = true
  
    // define the visible area of the projected shadow
    this.shadowLight.shadow.camera.left = -20
    this.shadowLight.shadow.camera.right = 20
    this.shadowLight.shadow.camera.top = 20
    this.shadowLight.shadow.camera.bottom = -20
    this.shadowLight.shadow.camera.near = 1
    this.shadowLight.shadow.camera.far = 100
  
    // define the resolution of the shadow; the higher the better, 
    // but also the more expensive and less performant
    // this.shadowLight.shadow.mapSize.width = 256
    // this.shadowLight.shadow.mapSize.height = 256
    this.shadowLight.shadow.mapSize.width = 512
    this.shadowLight.shadow.mapSize.height = 512
    // this.shadowLight.shadow.mapSize.width = 1024
    // this.shadowLight.shadow.mapSize.height = 1024
    // this.shadowLight.shadow.mapSize.width = 2048
    // this.shadowLight.shadow.mapSize.height = 2048
  
    // this.renderer.gammaInput = true
    // this.renderer.gammaOutput = true


    // const lensflare = new Lensflare()
    // lensflare.addElement(new LensflareElement(AssetManager.lensFlare1, 700, 0, this.shadowLight.color))
    // lensflare.addElement(new LensflareElement(AssetManager.lensFlare2, 60, 0.6))
    // lensflare.addElement(new LensflareElement(AssetManager.lensFlare2, 70, 0.7))
    // lensflare.addElement(new LensflareElement(AssetManager.lensFlare2, 120, 0.9))
    // lensflare.addElement(new LensflareElement(AssetManager.lensFlare2, 70, 1))
    // // lensflare.position.y -= 1000
    // this.scene.add(lensflare)
    // // this.shadowLight.add(lensflare)
    // this.lensflare = lensflare

    // to activate the lights, just add them to the scene
    // this.scene.add(this.ambientLight)
    // this.scene.add(this.hemisphereLight)
    this.scene.add(this.shadowLight)
    // this.scene.add(this.shadowLightHelper)
  }

}
